import { useRef, useState } from "react";
import { Bold, AlignLeft, AlignCenter, AlignRight, UploadCloud, Trash2, Eraser, RotateCw, SpellCheck } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DebouncedInput, DebouncedTextarea } from "@/components/DebouncedField";
import { BG_CATEGORY_MAP, CATEGORY_LABELS, QUICK_SWATCHES, fitLogoBox, isRotated180, ROTATE_180, FONT_OPTIONS, resolveFontStack, classifyFromStack } from "./designerUtils";
import { useFonts } from "@/context/FontsContext";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { resolvePantone } from "@/lib/pantone";

const FIELD_DEFAULT_SIZE = { productTitle: 13, size: 8, material: 6, pieces: 6, description: 6 };
const FIELD_LABEL = { productTitle: "props.fldProductTitle", size: "props.fldSize", material: "props.fldMaterial", pieces: "props.fldPieces", description: "props.fldDescription" };

function Wrap({ title, children }) {
  return (
    <div className="flex h-full w-72 shrink-0 flex-col border-l border-slate-200 bg-white" data-testid="properties-panel">
      <div className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</div>
      <div className="flex-1 overflow-y-auto bhf-scrollbar p-4 space-y-4">{children}</div>
    </div>
  );
}

function AlignBold({ align, bold, onAlign, onBold }) {
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <button onClick={onBold} data-testid="toggle-bold-button" className={`rounded-md border px-2 py-1.5 ${bold ? "border-indigo-300 bg-indigo-50 text-indigo-600" : "border-slate-200 text-slate-500"}`}>
        <Bold className="h-3.5 w-3.5" />
      </button>
      {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Icon]) => (
        <button key={a} onClick={() => onAlign(a)} data-testid={`align-${a}-button`} className={`rounded-md border px-2 py-1.5 ${align === a ? "border-indigo-300 bg-indigo-50 text-indigo-600" : "border-slate-200 text-slate-500"}`}>
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

function UploadButton({ label, onFile, testId }) {
  const ref = useRef(null);
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => ref.current?.click()} data-testid={testId}>
        <UploadCloud className="h-3.5 w-3.5" /> {label}
      </Button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
    </>
  );
}

function RemoveBgButton({ onClick, testId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      await onClick();
      toast.success(t("props.whiteBgRemoved"));
    } catch (e) {
      toast.error(t("props.removeBgFailed"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button variant="outline" size="sm" className="gap-1.5 text-xs text-slate-600" onClick={run} disabled={loading} data-testid={testId}>
      <Eraser className="h-3.5 w-3.5" /> {loading ? t("common.processing") : t("props.removeWhiteBg")}
    </Button>
  );
}

function PantoneSwatchPicker({ value, onChange }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const applyCode = () => {
    const hex = resolvePantone(code);
    if (!hex) { setError("未找到该潘通色号，请检查后重试"); return; }
    setError("");
    onChange(hex);
  };
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">{t("props.pantoneCode")}</Label>
        <div className="mt-1.5 flex gap-1.5">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("props.pantonePlaceholder")} className="h-8 flex-1 rounded-md border border-slate-200 px-2 text-xs" data-testid="pantone-code-input" onKeyDown={(e) => e.key === "Enter" && applyCode()} />
          <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={applyCode} data-testid="pantone-apply-button">应用</Button>
        </div>
        {error && <div className="mt-1 text-[11px] text-red-500" data-testid="pantone-error">{error}</div>}
        <div className="mt-1 text-[10px] text-slate-400">{t("props.pantoneNote")}</div>
      </div>
      <div>
        <Label className="text-xs">{t("props.customColor")}</Label>
        <input type="color" value={value && value.startsWith("#") ? value : "#ffffff"} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-9 w-full rounded border border-slate-200" data-testid="background-color-picker" />
      </div>
      <div>
        <Label className="text-xs">{t("props.quickSwatches")}</Label>
        <div className="mt-1.5 flex gap-2">
          {QUICK_SWATCHES.map((s) => (
            <button key={s.hex} onClick={() => onChange(s.hex)} title={s.label} data-testid={`quick-swatch-${s.hex.replace("#", "")}`} className="h-8 w-8 rounded-md border-2 border-slate-200 hover:border-indigo-400" style={{ background: s.hex }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FontSelect({ value, onChange, includeDefault, testId }) {
  const { t } = useTranslation();
  const { fonts, reload } = useFonts();
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const customOpts = (fonts || []).map((f) => ({ value: f.family, label: f.label || f.family }));
  const allOpts = [...FONT_OPTIONS, ...customOpts];
  const knownValues = new Set(allOpts.map((o) => o.value));
  const showExtra = value && !knownValues.has(value);

  const onPickFont = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["ttf", "otf"].includes(ext)) { toast.error(t("props.onlyTtfOtf")); return; }
    const form = new FormData();
    form.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/fonts", form, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
      onChange(data.family);
      toast.success(t("props.fontImportedApplied", { family: data.family }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("props.fontImportFailed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("props.font")}</Label>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={importing} data-testid={`${testId}-import`} className="text-[11px] font-medium text-indigo-600 hover:underline disabled:opacity-50">
          {importing ? t("settings.importing") : "+ " + t("settings.importFont")}
        </button>
      </div>
      <input ref={fileRef} type="file" accept=".ttf,.otf,font/ttf,font/otf" className="hidden" onChange={onPickFont} />
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300"
      >
        {includeDefault && <option value="">默认（品牌字体）</option>}
        {FONT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value} style={{ fontFamily: resolveFontStack(o.value) }}>{o.label}</option>
        ))}
        {customOpts.length > 0 && (
          <optgroup label={t("settings.importFont")}>
            {customOpts.map((o) => (
              <option key={o.value} value={o.value} style={{ fontFamily: o.value }}>{o.label}</option>
            ))}
          </optgroup>
        )}
        {showExtra && <option value={value}>{value}</option>}
      </select>
    </div>
  );
}

function SpellSuggestions({ result, onApply, onApplyAll }) {
  const { t } = useTranslation();
  if (!result || !result.issues?.length) return null;
  return (
    <div className="rounded-md border border-red-200 bg-red-50/70 p-2.5" data-testid="spell-suggestions">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-red-600">
        <SpellCheck className="h-3.5 w-3.5" /> {t("props.spellSuggest")}{result.language ? ` · ${result.language}` : ""}
      </div>
      <div className="space-y-1.5">
        {result.issues.map((iss, i) => (
          <div key={i} className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`spell-issue-${i}`}>
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-600 line-through">{iss.word}</span>
            <span className="text-slate-400">→</span>
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700">{iss.suggestion}</span>
            <button
              onClick={() => onApply?.(iss.word, iss.suggestion)}
              data-testid={`spell-apply-${i}`}
              className="ml-auto rounded border border-indigo-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-indigo-600 hover:bg-indigo-50"
            >{t("props.replace")}</button>
          </div>
        ))}
      </div>
      {result.issues.length > 1 && (
        <button
          onClick={() => onApplyAll?.(result.issues)}
          data-testid="spell-apply-all"
          className="mt-2 w-full rounded-md border border-indigo-300 bg-indigo-50 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-100"
        >{t("props.spellReplaceAll", { n: result.issues.length })}</button>
      )}
    </div>
  );
}

function RotateButton({ rotated, onClick, testId }) {
  const { t } = useTranslation();
  return (
    <Button variant="outline" size="sm" className={`gap-1.5 text-xs ${rotated ? "border-indigo-300 bg-indigo-50 text-indigo-600" : "text-slate-600"}`} onClick={onClick} data-testid={testId}>
      <RotateCw className="h-3.5 w-3.5" /> {rotated ? t("props.rotated180") : t("props.rotate180")}
    </Button>
  );
}

export default function PropertiesPanel({ mode, selection, customContent, product, brand, template, patchField, patchNested, updateAddedText, removeAddedText, updateAddedImage, removeAddedImage, updateOverlay, removeOverlay, uploadFile, uploadLogoFile, removeBgFile, patchLogoAndBox, onReplaceBackgroundFile, spellResult, onApplySpelling, onApplySpellingAll, textResetKey = 0 }) {
  const { t } = useTranslation();
  if (!selection) {
    return <div className="flex h-full w-72 shrink-0 items-center justify-center border-l border-slate-200 bg-white p-6 text-center text-xs text-slate-400" data-testid="properties-panel-empty">{t("props.selectElement")}</div>;
  }

  if (selection.kind === "background") {
    const category = product?.category;
    const catKey = BG_CATEGORY_MAP[category];
    return (
      <Wrap title={t("props.tBackground")}>
        {mode === "A" ? (
          <>
            <div>
              <Label className="text-xs">{t("props.brandBackgrounds")}</Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {["default", catKey].filter(Boolean).map((key) => {
                  const val = brand?.backgrounds?.[key];
                  if (!val) return null;
                  const isColor = val.startsWith("#");
                  return (
                    <button key={key} onClick={() => patchField("backgroundOverride", val)} data-testid={`bg-swatch-${key}`} className="h-10 w-10 rounded-md border-2 border-slate-200 hover:border-indigo-400" style={isColor ? { background: val } : { backgroundImage: `url(${val})`, backgroundSize: "cover" }} title={key} />
                  );
                })}
              </div>
            </div>
            <UploadButton label={t("props.replaceBackground")} testId="replace-background-button" onFile={async (f) => patchField("backgroundOverride", await uploadFile(f))} />
            <PantoneSwatchPicker value={customContent.backgroundOverride} onChange={(hex) => patchField("backgroundOverride", hex)} />
            {customContent.backgroundOverride && (
              <Button size="sm" variant="ghost" className="text-xs text-slate-500" onClick={() => patchField("backgroundOverride", undefined)} data-testid="reset-background-button">{t("props.useAutoBg")}</Button>
            )}
          </>
        ) : (
          <>
            <UploadButton label={t("props.replaceBackground")} testId="replace-background-button" onFile={onReplaceBackgroundFile} />
            <PantoneSwatchPicker value={customContent.backgroundColorOverride} onChange={(hex) => patchField("backgroundColorOverride", hex)} />
            {customContent.backgroundColorOverride && (
              <Button size="sm" variant="ghost" className="text-xs text-slate-500" onClick={() => patchField("backgroundColorOverride", undefined)} data-testid="reset-background-color-button">{t("props.clearColorUseImage")}</Button>
            )}
          </>
        )}
      </Wrap>
    );
  }

  if (selection.kind === "fixed" && selection.key === "logo") {
    const currentLogoUrl = customContent.logoUrlOverride || brand?.logos?.[customContent.logoKey || "main"];
    const logoBaseBox = customContent.positions?.logo || template?.elementConfig?.logo;
    return (
      <Wrap title={t("props.tLogo")}>
        <div className="flex flex-wrap gap-1.5">
          <UploadButton label={t("props.replaceLogo")} testId="replace-logo-button" onFile={async (f) => {
            const { url, width, height } = await uploadLogoFile(f);
            const box = fitLogoBox(logoBaseBox, width, height);
            patchLogoAndBox(url, box);
          }} />
          {currentLogoUrl && (
            <RemoveBgButton testId="remove-bg-logo-button" onClick={async () => patchField("logoUrlOverride", await removeBgFile(currentLogoUrl))} />
          )}
        </div>
        <div className="text-[10px] text-slate-400">{t("props.logoNote")}</div>
        <div>
          <Label className="text-xs">{t("props.logoVariant")}</Label>
          <div className="mt-1.5 flex gap-2">
            {["main", "white", "black"].map((k) => (
              <button key={k} onClick={() => patchField("logoKey", k)} data-testid={`logo-variant-${k}`} className={`rounded-md border px-2.5 py-1 text-xs capitalize ${customContent.logoKey === k || (!customContent.logoKey && k === "main") ? "border-indigo-300 bg-indigo-50 text-indigo-600" : "border-slate-200 text-slate-500"}`}>{k}</button>
            ))}
          </div>
        </div>
      </Wrap>
    );
  }

  if (selection.kind === "fixed" && selection.key === "productImage") {
    return (
      <Wrap title={t("props.tProductImage")}>
        <UploadButton label={t("props.replaceImage")} testId="replace-product-image-button" onFile={async (f) => patchField("productImageOverride", await uploadFile(f))} />
      </Wrap>
    );
  }

  if (selection.kind === "fixed" && selection.key === "upc") {
    const value = customContent.customUPC ?? product?.upc ?? "";
    return (
      <Wrap title={t("props.tUpc")}>
        <div><Label className="text-xs">{t("props.upcNumber")}</Label><DebouncedInput key="upc" className="mt-1.5" initialValue={value} onCommit={(v) => patchField("customUPC", v)} data-testid="upc-content-input" /></div>
      </Wrap>
    );
  }

  if (selection.kind === "fixed" && ["productTitle", "size", "material", "pieces", "description"].includes(selection.key)) {
    const key = selection.key;
    const productKeyMap = { productTitle: "productName" };
    const value = customContent[key] ?? product?.[productKeyMap[key] || key] ?? "";
    const fontSize = customContent.fontSize?.[key] ?? FIELD_DEFAULT_SIZE[key];
    const bold = customContent.bold?.[key] ?? (key === "productTitle");
    const align = customContent.align?.[key] ?? "left";
    return (
      <Wrap title={t(FIELD_LABEL[key])}>
        <div>
          <Label className="text-xs">{t("props.textContent")}</Label>
          <DebouncedTextarea key={`${key}-${textResetKey}`} className="mt-1.5" rows={key === "description" ? 4 : 2} initialValue={value} onCommit={(v) => patchField(key, v)} data-testid={`${key}-content-input`} />
        </div>
        <div>
          <Label className="text-xs">{t("props.fontSize")}</Label>
          <DebouncedInput key={`fontsize-${key}`} type="number" className="mt-1.5" initialValue={fontSize} onCommit={(v) => patchNested("fontSize", key, Number(v))} data-testid={`${key}-fontsize-input`} />
        </div>
        <FontSelect includeDefault value={customContent.fontFamily?.[key] ?? ""} onChange={(v) => patchNested("fontFamily", key, v)} testId={`${key}-font-input`} />
        <div>
          <Label className="text-xs">{t("props.style")}</Label>
          <AlignBold align={align} bold={bold} onAlign={(a) => patchNested("align", key, a)} onBold={() => patchNested("bold", key, !bold)} />
        </div>
        <SpellSuggestions result={spellResult} onApply={onApplySpelling} onApplyAll={onApplySpellingAll} />
      </Wrap>
    );
  }

  if (selection.kind === "text") {
    const item = (customContent.addedTexts || [])[selection.key];
    if (!item) return null;
    return (
      <Wrap title={t("props.tAddedText")}>
        <div><Label className="text-xs">{t("props.textContent")}</Label><DebouncedTextarea key={`text-${selection.key}-${textResetKey}`} className="mt-1.5" rows={3} initialValue={item.text} onCommit={(v) => updateAddedText(selection.key, { text: v })} data-testid="added-text-content-input" /></div>
        <div><Label className="text-xs">{t("props.fontSize")}</Label><DebouncedInput key={`text-fontsize-${selection.key}`} type="number" className="mt-1.5" initialValue={item.fontSize} onCommit={(v) => updateAddedText(selection.key, { fontSize: Number(v) })} data-testid="added-text-fontsize-input" /></div>
        <FontSelect value={classifyFromStack(item.font)} onChange={(v) => updateAddedText(selection.key, { font: resolveFontStack(v) })} testId="added-text-font-input" />
        <div><Label className="text-xs">{t("props.color")}</Label><input type="color" className="mt-1.5 h-9 w-full rounded border border-slate-200" value={item.color} onChange={(e) => updateAddedText(selection.key, { color: e.target.value })} data-testid="added-text-color-input" /></div>
        <div><Label className="text-xs">{t("props.style")}</Label><AlignBold align={item.align} bold={item.bold} onAlign={(a) => updateAddedText(selection.key, { align: a })} onBold={() => updateAddedText(selection.key, { bold: !item.bold })} /></div>
        <SpellSuggestions result={spellResult} onApply={onApplySpelling} onApplyAll={onApplySpellingAll} />
        <div className="grid grid-cols-2 gap-2">
          {["x", "y", "width", "height"].map((f) => (
            <div key={f}><Label className="text-[10px] uppercase">{f}</Label><DebouncedInput key={`text-${f}-${selection.key}`} type="number" className="mt-1" initialValue={item[f]} onCommit={(v) => updateAddedText(selection.key, { [f]: Number(v) })} /></div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="w-full gap-1.5 text-red-500" onClick={() => removeAddedText(selection.key)} data-testid="remove-added-text-button"><Trash2 className="h-3.5 w-3.5" /> {t("props.remove")}</Button>
      </Wrap>
    );
  }

  if (selection.kind === "image") {
    const item = (customContent.addedImages || [])[selection.key];
    if (!item) return null;
    return (
      <Wrap title={t("props.tAddedImage")}>
        <UploadButton label={t("props.replaceImage")} testId="added-image-replace-button" onFile={async (f) => updateAddedImage(selection.key, { url: await uploadFile(f) })} />
        <div className="grid grid-cols-2 gap-2">
          {["x", "y", "width", "height"].map((f) => (
            <div key={f}><Label className="text-[10px] uppercase">{f}</Label><DebouncedInput key={`image-${f}-${selection.key}`} type="number" className="mt-1" initialValue={item[f]} onCommit={(v) => updateAddedImage(selection.key, { [f]: Number(v) })} /></div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="w-full gap-1.5 text-red-500" onClick={() => removeAddedImage(selection.key)} data-testid="remove-added-image-button"><Trash2 className="h-3.5 w-3.5" /> {t("props.remove")}</Button>
      </Wrap>
    );
  }

  if (selection.kind === "overlay") {
    const item = (customContent.overlays || [])[selection.key];
    if (!item) return null;
    return (
      <Wrap title={CATEGORY_LABELS[item.category] || `${item.type} overlay`}>
        {item.type === "text" && (
          <>
            <div><Label className="text-xs">{t("props.textContent")}</Label><DebouncedTextarea key={`overlay-text-${selection.key}-${textResetKey}`} className="mt-1.5" rows={3} initialValue={item.text} onCommit={(v) => updateOverlay(selection.key, { text: v })} data-testid="overlay-text-content-input" /></div>
            <div><Label className="text-xs">{t("props.fontSize")}</Label><DebouncedInput key={`overlay-fontsize-${selection.key}`} type="number" className="mt-1.5" initialValue={item.fontSize} onCommit={(v) => updateOverlay(selection.key, { fontSize: Number(v) })} data-testid="overlay-text-fontsize-input" /></div>
            <FontSelect value={classifyFromStack(item.font)} onChange={(v) => updateOverlay(selection.key, { font: resolveFontStack(v) })} testId="overlay-text-font-input" />
            <div><Label className="text-xs">{t("props.color")}</Label><input type="color" className="mt-1.5 h-9 w-full rounded border border-slate-200" value={item.color} onChange={(e) => updateOverlay(selection.key, { color: e.target.value })} /></div>
            <div><Label className="text-xs">{t("props.style")}</Label><AlignBold align={item.align} bold={item.bold} onAlign={(a) => updateOverlay(selection.key, { align: a })} onBold={() => updateOverlay(selection.key, { bold: !item.bold })} /></div>
            <SpellSuggestions result={spellResult} onApply={onApplySpelling} onApplyAll={onApplySpellingAll} />
          </>
        )}
        {item.type === "upc" && (
          <div><Label className="text-xs">{t("props.upcNumber")}</Label><DebouncedInput key={`overlay-upc-${selection.key}`} className="mt-1.5" initialValue={item.text} onCommit={(v) => updateOverlay(selection.key, { text: v })} data-testid="overlay-upc-input" /></div>
        )}
        {(item.type === "image" || item.type === "logo") && (
          <div className="flex flex-wrap gap-1.5">
            <UploadButton label={`Replace ${item.type}`} testId="overlay-replace-image-button" onFile={async (f) => {
              if (item.type === "logo") {
                const { url, width, height } = await uploadLogoFile(f);
                const box = fitLogoBox({ x: item.x ?? 0, y: item.y ?? 0, width: item.width ?? 200, height: item.height ?? 120 }, width, height);
                updateOverlay(selection.key, box ? { url, ...box } : { url });
              } else {
                updateOverlay(selection.key, { url: await uploadFile(f) });
              }
            }} />
            {item.type === "logo" && item.url && (
              <RemoveBgButton testId="remove-bg-overlay-button" onClick={async () => updateOverlay(selection.key, { url: await removeBgFile(item.url) })} />
            )}
          </div>
        )}
        {item.type === "cover" && (
          <div><Label className="text-xs">{t("props.coverColor")}</Label><input type="color" className="mt-1.5 h-9 w-full rounded border border-slate-200" value={item.color} onChange={(e) => updateOverlay(selection.key, { color: e.target.value })} data-testid="overlay-cover-color-input" /></div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {["x", "y", "width", "height"].map((f) => (
            <div key={f}><Label className="text-[10px] uppercase">{f}</Label><DebouncedInput key={`overlay-${f}-${selection.key}`} type="number" className="mt-1" initialValue={item[f]} onCommit={(v) => updateOverlay(selection.key, { [f]: Number(v) })} data-testid={`overlay-${f}-input`} /></div>
          ))}
        </div>
        {["text", "image", "logo", "upc"].includes(item.type) && (
          <RotateButton rotated={isRotated180(item.transform)} testId="overlay-rotate-180-button" onClick={() => updateOverlay(selection.key, { transform: isRotated180(item.transform) ? null : ROTATE_180 })} />
        )}
        <Button variant="outline" size="sm" className="w-full gap-1.5 text-red-500" onClick={() => removeOverlay(selection.key)} data-testid="remove-overlay-button"><Trash2 className="h-3.5 w-3.5" /> {t("props.remove")}</Button>
      </Wrap>
    );
  }

  return null;
}
